/** @jsxImportSource @emotion/react */
import { useState, useRef } from "react";
import { FileUpload } from "primereact/fileupload";
import { ProgressBar } from "primereact/progressbar";
import { Toast } from "primereact/toast";
import Boton from "../boton";
import { subirArchivosPesadosS3 } from "../../utils";

// Componente reutilizable para subir archivos (incluso PESADOS) DIRECTO a S3 vía
// presigned URL. Empaqueta todo el comportamiento común: file picker, PUT directo
// a S3, barra de progreso con nombre del archivo actual y toasts de éxito/error.
//
// Cada lugar que sube archivos solo necesita decir:
//   - a qué carpeta de S3 van (ubicacion)
//   - el endpoint de presign genérico de su rol (presignEndpoint)
//   - su endpoint de confirmación propio (confirmarEndpoint)
//   - qué hacer con los registros creados (onSubido)
//
// Props:
//   presignEndpoint   ej "admin/archivo/presign" (genérico por rol)
//   confirmarEndpoint ej `admin/proceso/${id}/archivo/confirmar`
//   ubicacion         carpeta de S3, ej "archivosprocesos" (debe estar en la
//                     whitelist del backend: UBICACIONES_S3_PERMITIDAS)
//   onSubido          (creados) => void  — registros creados que devuelve confirmar
//   datosConfirmar    objeto extra opcional para el body de confirmar
//   multiple          permitir varios archivos a la vez (default false)
//   accept            filtro de tipos para el input, ej "image/*,.pdf"
//   icono             clase del ícono del botón solo-ícono (default "pi pi-plus").
//                     No aplica cuando hay chooseLabel: el botón con label es un
//                     Boton estándar, sin ícono.
//   disabled          deshabilita el botón
//   onError           (error) => void  — opcional, además del toast de error
//   progresoInline    si es true, muestra la barra de progreso inline dentro del
//                     contenido (debajo del picker). Por defecto (false) la muestra
//                     como tarjeta flotante fija abajo a la derecha, independiente
//                     del layout — recomendado para consistencia entre secciones.
//   chooseLabel       texto del botón. Si se omite (""), el botón es solo-ícono
//                     borderless (como en la card de archivos del proceso). Si se
//                     pasa un texto, se renderiza un Boton estándar con ese label
//                     (ej "Subir foto") que dispara un input de archivo oculto.
//   transparente      se pasa al Boton del modo con label (variante transparente).
//   texto             se pasa al Boton del modo con label (variante de texto).
//   capture           se pasa al input (ej "environment" para abrir la cámara
//                     trasera en mobile).
export default function SubidorArchivos({
  presignEndpoint,
  confirmarEndpoint,
  ubicacion,
  onSubido,
  datosConfirmar = null,
  multiple = false,
  accept,
  icono = "pi pi-plus",
  disabled = false,
  onError,
  progresoInline = false,
  chooseLabel = "",
  capture,
  transparente = false,
  texto = false,
}) {
  const esIconoSolo = !chooseLabel;
  const [uploading, setUploading] = useState(false);
  const [progreso, setProgreso] = useState(0);
  const [archivoEnSubida, setArchivoEnSubida] = useState(null);

  const toast = useRef(null);
  const fileUploadRef = useRef(null);
  const inputRef = useRef(null);

  function handleUpload(event) {
    const files = event.files || [];
    if (!files.length) return;

    // Capturamos los archivos y limpiamos el FileUpload de inmediato. Así, en
    // modo basic, PrimeReact no reemplaza el label del botón por el nombre del
    // primer archivo: se mantiene el chooseLabel original (con el spinner al lado
    // mientras se sube, vía el estado `uploading`).
    const archivos = Array.from(files);
    fileUploadRef.current?.clear();
    subir(archivos);
  }

  // El modo con label usa un input nativo oculto (el botón visible es un Boton).
  function archivosElegidos(event) {
    const archivos = Array.from(event.target.files || []);
    event.target.value = "";
    if (!archivos.length) return;
    subir(archivos);
  }

  function subir(archivos) {
    setUploading(true);
    setProgreso(0);
    setArchivoEnSubida(null);

    subirArchivosPesadosS3({
      presignEndpoint,
      confirmarEndpoint,
      archivos,
      datosPresign: { ubicacion },
      datosConfirmar,
      onProgress: (porcentaje, info) => {
        setProgreso(porcentaje);
        setArchivoEnSubida(info);
      },
    })
      .then((creados) => {
        setUploading(false);
        setProgreso(0);
        toast.current?.show({
          severity: "success",
          summary: "Subido",
          detail:
            Array.isArray(creados) && creados.length > 1
              ? `${creados.length} archivos subidos exitosamente`
              : "Archivo subido exitosamente",
        });
        fileUploadRef.current?.clear();
        if (onSubido) onSubido(creados);
      })
      .catch((error) => {
        setUploading(false);
        setProgreso(0);
        fileUploadRef.current?.clear();
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error?.[0]?.msg || "Error al subir archivo",
          sticky: true,
        });
        if (onError) onError(error);
      });
  }

  return (
    <>
      <Toast ref={toast} position="top-center" />

      {/* Loader propio del modo solo-ícono mientras sube. No se usa el ícono
          del FileUpload porque PrimeReact le asigna una key nueva en cada
          render (getJSXIcon), y con las actualizaciones de progreso el nodo se
          recrea varias veces por segundo reiniciando la animación (el spinner
          "salta"). Este elemento es estable y gira suave. El modo con label no
          lo necesita: el Boton tiene su propia prop loader. */}
      {uploading && esIconoSolo && (
        <span
          css={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
            padding: "0.5rem",
            color: "var(--negro)",
            // Mismo tamaño de fuente y alto de línea que el botón (p-button-sm)
            // para que el swap botón → loader no mueva el layout
            fontSize: "0.875rem",
            lineHeight: 1,
          }}
        >
          <i className="pi pi-spin pi-spinner" css={{ fontSize: "0.875rem" }}></i>
        </span>
      )}

      {esIconoSolo ? (
        <FileUpload
          ref={fileUploadRef}
          mode="basic"
          name="archivo"
          multiple={multiple}
          accept={accept}
          capture={capture}
          customUpload
          onSelect={handleUpload}
          chooseOptions={{
            icon: icono,
            iconOnly: true,
            className: "p-button-text p-button-sm",
          }}
          disabled={disabled || uploading}
          css={[
            // Mientras sube, el botón se oculta (queda el loader de arriba en su
            // lugar) pero sigue montado para conservar la ref
            uploading && { display: "none" },
            {
              ".p-button": {
                backgroundColor: "transparent !important",
                color: "var(--negro) !important",
                border: "none !important",
                padding: "0.5rem !important",
              },
              ".p-button:hover": {
                backgroundColor: "var(--surface-100) !important",
              },
              ".p-button .p-button-icon": {
                marginRight: "0 !important",
              },
              // En modo basic, PrimeReact pone el nombre del archivo como
              // label del botón. Lo ocultamos para dejar solo el ícono/loader.
              ".p-button-label": {
                display: "none !important",
              },
            },
          ]}
        />
      ) : (
        // Modo con label: un Boton estándar (misma altura y estética que el
        // resto de los botones de la app) que dispara el input nativo oculto.
        // Mientras sube, el Boton queda con su propio loader prendido.
        <>
          <input
            ref={inputRef}
            type="file"
            multiple={multiple}
            accept={accept}
            capture={capture}
            onChange={archivosElegidos}
            css={{ display: "none" }}
          />
          <Boton
            callback={() => inputRef.current?.click()}
            disabled={disabled || uploading}
            loader={uploading}
            transparente={transparente}
            texto={texto}
          >
            {chooseLabel}
          </Boton>
        </>
      )}

      {uploading && (
        <ProgresoSubida
          progreso={progreso}
          archivoEnSubida={archivoEnSubida}
          inline={progresoInline}
        />
      )}
    </>
  );
}

// Muestra el progreso de la subida. El mismo contenido (texto + barra) se renderiza
// inline (dentro del flujo del contenido) o como tarjeta flotante fija abajo a la
// derecha, según la prop `inline`.
function ProgresoSubida({ progreso, archivoEnSubida, inline }) {
  const texto =
    progreso >= 100
      ? "Procesando…"
      : archivoEnSubida
        ? `Subiendo ${
            archivoEnSubida.total > 1
              ? `(${archivoEnSubida.numero}/${archivoEnSubida.total}) `
              : ""
          }${archivoEnSubida.nombre}… ${progreso}%`
        : `Subiendo archivo… ${progreso}%`;

  const contenido = (
    <>
      <small
        css={{
          display: "block",
          marginBottom: "0.5rem",
          color: "var(--text-color-secondary)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {texto}
      </small>
      <ProgressBar
        value={progreso}
        showValue={false}
        css={{
          height: "0.6rem",
          ".p-progressbar-value": {
            backgroundColor: "var(--principal) !important",
          },
        }}
      />
    </>
  );

  if (inline) {
    return <div css={{ margin: "0.75em 0 1em" }}>{contenido}</div>;
  }

  // Tarjeta flotante fija: position:fixed la hace independiente del layout donde
  // se monte el componente, así se ve igual sin importar dónde esté el botón.
  return (
    <div
      css={{
        position: "fixed",
        bottom: "1.5rem",
        right: "1.5rem",
        zIndex: 1100,
        width: "320px",
        maxWidth: "calc(100vw - 3rem)",
        background: "white",
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.18)",
        borderRadius: "8px",
        padding: "0.9rem 1rem",
      }}
    >
      {contenido}
    </div>
  );
}
